// Single source of truth for the words in ManagingResources (portal DOM) and
// ManagingResourcesPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF managing chapter resources";

export const cover = {
  eyebrow: "Admin · 08",
  number: "08",
  title: "Managing chapter resources",
  summary: "Adding, editing, and bulk-importing the funding orgs, incubators, and community resources your chapter points founders toward.",
  accent: "tangerine",
};

export const blocks = [
  {
    type: "lede",
    text: "The Resources tab is the index a founder lands on when they ask the resource directory \"where do I start?\" What you put in here is what gets surfaced. The cleaner and tighter the entries, the more useful the matches.",
  },
  { type: "heading", level: 1, text: "The two ways to add resources" },
  {
    type: "p",
    runs: [
      "Most chapters use both. ",
      { b: "Add Resource" },
      " is best when you're capturing a single new org you just learned about.",
      { b: " Upload CSV" },
      " is best when you're seeding a new chapter or doing a quarterly cleanup pass on twenty rows at once. Same data model, same Firestore collection; pick the one that matches the shape of your work.",
    ],
    pdfRuns: [
      "Most chapters use both. ",
      { b: "Add Resource" },
      " is best when you're capturing a single new org you just learned about. ",
      { b: "Upload CSV" },
      " is best when you're seeding a new chapter or doing a quarterly cleanup pass on twenty rows at once. Same data model, same Firestore collection; pick the one that matches the shape of your work.",
    ],
  },
  {
    type: "callout",
    label: "You only see your chapter",
    runs: [
      "The Chapter column is pinned to your chapter; you can't accidentally edit Denver's resources from the WNY portal, and uploads ignore any other chapter named in a CSV. Super admins can move between chapters freely.",
    ],
  },
  { type: "heading", level: 1, text: "Adding one resource at a time" },
  {
    type: "p",
    runs: [
      "Click ",
      { b: "+ Add Resource" },
      ". The form opens with three sections: Basics, Focus & Reach, and Details. Three fields are required (Resource Name, Type, and Business Stage), and the rest sharpen the match.",
    ],
    pdfRuns: [
      "Click + Add Resource. The form opens with three sections: Basics, Focus & Reach, and Details. Three fields are required (Resource Name, Type, and Business Stage), and the rest sharpen the match.",
    ],
  },
  { type: "heading", level: 2, text: "Resource Name" },
  {
    type: "p",
    runs: [
      "2-80 characters. Use the name a founder would Google. \"43North Accelerator,\" not \"43N.\" If the program is part of a parent org, lead with the program: \"Buffalo Urban League: Start Up Together,\" not \"Buffalo Urban League.\" That's how the cards render and how the search ranks.",
    ],
  },
  { type: "heading", level: 2, text: "Type" },
  {
    type: "p",
    runs: [
      "Pick from the dropdown: Funding, Incubator/Accelerator, Mentorship, Community, and so on. Don't invent new types in the CSV; the directory keys off this field to color-code cards and the AI uses it as a high-signal filter. If the type you need genuinely doesn't exist, message a super admin instead of forcing a square peg.",
    ],
  },
  { type: "heading", level: 2, text: "Business Stage" },
  {
    type: "p",
    runs: [
      "Ideation, Early, Growth, Established, or All. ",
      { b: "Use \"All\" sparingly" },
      ": it means the resource genuinely serves every stage, which is rare. A general-purpose coworking space is \"All.\" A pre-seed accelerator is \"Early.\" When in doubt, pick the narrowest stage that's accurate, because \"All\" entries surface in every search and dilute results.",
    ],
  },
  { type: "heading", level: 2, text: "Focus Area", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Optional but recommended. A short phrase, max 80 chars: \"Tech startups,\" \"Minority-led small business,\" \"Health/Medtech.\" This is the line that appears under the resource name on cards, so write it like a tag, not a sentence.",
    ],
  },
  { type: "heading", level: 2, text: "Counties Served" },
  {
    type: "p",
    runs: [
      "Comma-separated list, or \"All 8 counties,\" or \"Global\" for anything that doesn't care about geography. Helps founders understand whether they're eligible before they click through.",
    ],
  },
  { type: "heading", level: 2, text: "Average check size" },
  {
    type: "p",
    runs: [
      "Free-form. \"$50K-$250K,\" \"$1M,\" or \"NA\" if money isn't the product. Founders looking at capital sources scan this column first.",
    ],
  },
  { type: "heading", level: 2, text: "Relocation required?" },
  {
    type: "p",
    runs: [
      "Yes or No. Most resources don't require it; the ones that do (43North is the canonical example) need to flag it loudly so a Buffalo founder doesn't apply to a program that wants them in another city.",
    ],
  },
  { type: "heading", level: 2, text: "URL" },
  {
    type: "p",
    runs: [
      "Full URL with the ",
      { code: "https://" },
      " prefix. Cards render a \"Visit\" link that opens this in a new tab.",
    ],
  },
  { type: "heading", level: 2, text: "Expanded Details" },
  {
    type: "p",
    runs: [
      "This is the field that makes the resource directory useful or useless. ",
      { b: "1-3 sentences. ~200-500 characters hits the sweet spot." },
      " Tell a founder what they actually get, who it's for, and what makes it different from the next resource in the same category.",
    ],
  },
  {
    type: "p",
    runs: [
      "The form shows a live character count. Aqua means you're in the sweet spot. The hard cap is 800 characters; anything longer and you're writing copy, not a description.",
    ],
  },
  {
    type: "callout",
    label: "Avoid marketing language",
    runs: [
      "\"Innovative,\" \"cutting-edge,\" \"world-class.\" The directory will down-rank vague descriptions because they don't help it match. Write it the way you'd describe the resource to a friend over coffee.",
    ],
  },
  { type: "heading", level: 2, text: "A good Expanded Details example" },
  {
    type: "exampleBox",
    text: "\"Annual competition awarding $1M each to 5 startups. Companies relocate to Buffalo for 12 months and receive a year of free workspace, mentorship, and access to a network of investors. Best fit for traction-stage tech founders comfortable moving operations.\"",
  },
  {
    type: "p",
    runs: [
      "That's 47 words, 280 characters. It tells a founder what they get, what's required, and who it's for. The directory will match it precisely because the language is concrete.",
    ],
    pdfRuns: [
      "47 words, 280 characters. It tells a founder what they get, what's required, and who it's for. The directory will match it precisely because the language is concrete.",
    ],
  },
  { type: "heading", level: 1, text: "Bulk import via CSV", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Use this when you're seeding a new chapter, importing a list from a spreadsheet, or running a cleanup pass on a dozen rows. Two-step flow: download a template, edit it, upload.",
    ],
  },
  { type: "heading", level: 2, text: "Step 1: Download the template" },
  {
    type: "p",
    runs: [
      "Click ",
      { b: "Download Template" },
      ". The CSV that lands in your downloads folder has three things:",
    ],
    pdfRuns: [
      "Click Download Template. The CSV that lands in your downloads folder has three things:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: ["A row of column headers (don't change these names)."] },
      {
        runs: [
          "Five comment rows starting with ",
          { code: "#" },
          " with the allowed values for Type, Business Stage, and length guidance for Expanded Details.",
        ],
      },
      { runs: ["Two example rows with realistic data, already pinned to your chapter."] },
    ],
  },
  {
    type: "p",
    runs: [
      "Open it in Google Sheets, Numbers, or Excel. Use the example rows as a model, then delete them (and the comment rows) before uploading. Anything that starts with ",
      { code: "#" },
      " is ignored on parse, so leaving them in won't break the import, but it's cleaner to delete them.",
    ],
    pdfRuns: [
      "Open it in Google Sheets, Numbers, or Excel. Use the example rows as a model, then delete them (and the comment rows) before uploading. Anything that starts with # is ignored on parse, so leaving them in won't break the import, but it's cleaner to delete them.",
    ],
  },
  { type: "heading", level: 2, text: "Step 2: Fill in your rows" },
  {
    type: "p",
    runs: [
      "Required columns: ",
      { code: "Resource" },
      ", ",
      { code: "Type" },
      ", ",
      { code: "Business Stage" },
      ". The Chapter column is filled automatically with your chapter name on import (you can leave it blank or wrong; it'll be overwritten). Everything else is optional but, again, the more you fill in the better the resource directory gets.",
    ],
  },
  { type: "heading", level: 2, text: "Step 3: Upload" },
  {
    type: "p",
    runs: [
      "Click ",
      { b: "Upload CSV" },
      ", pick the file. The portal parses it on the spot and shows a preview panel with two columns of information:",
    ],
    pdfRuns: [
      "Click Upload CSV, pick the file. The portal parses it on the spot and shows a preview panel with two columns of information:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Valid rows" },
          ": these will write to Firestore on commit. Each one shows the resource name, type, stage, chapter, and focus area.",
        ],
      },
      {
        runs: [
          { b: "Skipped rows" },
          ": these failed validation. Each one tells you why (\"Type 'Coffee' not in allowed list,\" \"Resource Name missing,\" \"URL must start with http:// or https://\").",
        ],
      },
    ],
  },
  {
    type: "p",
    runs: [
      "Nothing has been written yet. Read the skipped rows, fix them in your spreadsheet, and re-upload. Or click ",
      { b: "Cancel" },
      " and start over.",
    ],
    pdfRuns: [
      "Nothing has been written yet. Read the skipped rows, fix them in your spreadsheet, and re-upload. Or click Cancel and start over.",
    ],
  },
  { type: "heading", level: 2, text: "Step 4: Commit" },
  {
    type: "p",
    runs: [
      "Click ",
      { b: "Import N resources" },
      " at the top of the preview. The portal writes everything in batches of 400 (a Firestore limit) and refreshes the table. New resources are live to the resource directory and chapter pages within a minute.",
    ],
    pdfRuns: [
      "Click Import N resources at the top of the preview. The portal writes everything in batches of 400 (a Firestore limit) and refreshes the table. New resources are live to the resource directory and chapter pages within a minute.",
    ],
  },
  {
    type: "callout",
    label: "Validation rules",
    runs: [
      "A row is skipped if Resource, Type, or Business Stage is missing; if Type isn't in the allowed list; if Business Stage isn't one of the five values; if URL doesn't start with http:// or https://; or if any field exceeds its length cap (Expanded Details: 800 chars). Everything else is permissive.",
    ],
  },
  { type: "heading", level: 1, text: "Editing & deleting", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Each row in the table has Edit and Delete buttons in the Actions column. Edit opens the same form you used to create the resource, pre-filled. Delete asks once and then removes the resource immediately; it disappears from the resource directory and chapter pages on the next page load. There's no soft-delete and no undo.",
    ],
  },
  { type: "heading", level: 1, text: "Filters" },
  {
    type: "p",
    runs: [
      "The toolbar above the table has search, chapter (super admins only), type, and stage filters. They stack; searching \"buffalo\" with stage \"Early\" returns rows that match both. Use these to find the resource you want to edit, not to scroll the whole list.",
    ],
  },
  { type: "heading", level: 1, text: "What happens when you save" },
  {
    type: "p",
    runs: [
      "Resources are written to the ",
      { code: "/resources" },
      " Firestore collection. From there:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          "The chapter page (e.g., ",
          { code: "/wny" },
          ") reads them on render and lists them by type.",
        ],
      },
      {
        runs: [
          "The resource directory on ",
          { code: "/resources" },
          " lists the full collection with chapter, stage, and type filters.",
        ],
      },
      {
        runs: [
          "Static chapter HTML pages get the data via the chapter-hydration script; propagation is near-instant for the resource directory, ~5 minutes for the static pages (cache).",
        ],
      },
    ],
  },
  { type: "heading", level: 1, text: "Routine maintenance" },
  { type: "p", runs: ["A reasonable cadence for a chapter director:"] },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Quarterly" },
          ": review the full list. Anything that's gone defunct? Anything new in town that should be here? Ten minutes, on the same calendar slot every quarter.",
        ],
      },
      {
        runs: [
          { b: "Annually" },
          ": re-read the Expanded Details on every entry. Programs change focus, check sizes shift, application windows move. Stale copy is worse than no copy.",
        ],
      },
      {
        runs: [
          { b: "As needed" },
          ": when an LP introduces you to an org you didn't know about, add it that day. Don't batch.",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "The point of all of this",
    runs: [
      "A founder lands on the resource directory, types their zip, picks \"Capital\" and \"Mentors,\" and gets a six-resource shortlist with a one-line \"here's why\" for each. That experience is only as good as the data behind it. Your quarterly ten minutes here is the highest-leverage admin work in the portal.",
    ],
  },
];
